import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { useMasterOptionSearch, type MasterOptionKind } from '@/hooks/useMasterOptionSearch';
import { useSelectLabels } from '@/hooks/useSelectLabels';
import { useTranslation } from 'react-i18next';

export interface MasterDataSelectProps {
  kind: MasterOptionKind;
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  hint?: string;
  className?: string;
  name?: string;
}

function useMasterDataDefaults(kind: MasterOptionKind) {
  const selectLabels = useSelectLabels();
  const { t } = useTranslation('common');
  switch (kind) {
    case 'countries':
      return {
        label: t('company.country'),
        placeholder: selectLabels.selectCountry,
        searchPlaceholder: selectLabels.searchCountries,
        emptyMessage: selectLabels.noCountries,
      };
    case 'currencies':
      return {
        label: t('company.currency'),
        placeholder: selectLabels.selectCurrency,
        searchPlaceholder: selectLabels.searchCurrencies,
        emptyMessage: selectLabels.noCurrencies,
      };
    case 'timezones':
      return {
        label: t('company.timezone'),
        placeholder: selectLabels.selectTimezone,
        searchPlaceholder: selectLabels.searchTimezones,
        emptyMessage: selectLabels.noTimezones,
      };
    case 'languages':
      return {
        label: t('company.language'),
        placeholder: selectLabels.selectLanguage,
        searchPlaceholder: selectLabels.searchLanguages,
        emptyMessage: selectLabels.noLanguages,
      };
  }
}

export function MasterDataSelect({
  kind,
  label,
  value = '',
  onChange,
  placeholder,
  disabled,
  required,
  error,
  hint,
  className,
  name,
}: MasterDataSelectProps) {
  const copy = useMasterDataDefaults(kind);
  const { options, loading, searching, onSearchQuery } = useMasterOptionSearch(kind, value);

  return (
    <SearchableSelect
      label={label ?? copy.label}
      name={name}
      required={required}
      error={error}
      hint={hint}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder ?? copy.placeholder}
      disabled={disabled}
      loading={loading}
      searching={searching}
      className={className}
      searchPlaceholder={copy.searchPlaceholder}
      emptyMessage={copy.emptyMessage}
      filterLocally={kind === 'languages'}
      onSearchQuery={kind === 'languages' ? undefined : onSearchQuery}
    />
  );
}

export const CountrySelect = (props: Omit<MasterDataSelectProps, 'kind'>) => (
  <MasterDataSelect kind="countries" {...props} />
);
export const CurrencySelect = (props: Omit<MasterDataSelectProps, 'kind'>) => (
  <MasterDataSelect kind="currencies" {...props} />
);
export const TimezoneSelect = (props: Omit<MasterDataSelectProps, 'kind'>) => (
  <MasterDataSelect kind="timezones" {...props} />
);

export function LanguageSelect(props: Omit<MasterDataSelectProps, 'kind'>) {
  const { t } = useTranslation('common');
  return (
    <MasterDataSelect
      kind="languages"
      label={props.label ?? t('company.language')}
      {...props}
    />
  );
}
